import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import {
  arrayOf,
  number,
  bool,
  params,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { Link, Popover, Typography } from '@one-thd/sui-atomic-components';
import { Clock } from '@one-thd/sui-icons';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  getEndDateWithTimezoneOffset,
  getLongDescription,
  getPromoToDisplay,
  getShortDescription,
  getSpecialBuy,
  getStartDate,
  hasSpecialBuy
} from '../utils/promo-helpers';
import { analyticsPromoClickEvent } from '../utils/utils';
import { PromoModelProvider } from '../contexts/PromoModelProvider';
import { PromoMessageProvider } from '../contexts/PromoMessageProvider';
import { PromoMessaging } from './PromoMessaging';
import { CONDITIONAL_PROMO_PAGES, FEATURE_SWITCH_KEYS } from '../utils/constants';

const Promotion = ({
  itemId,
  hideConditionalPromoDescription,
  pageType,
  promotionData
}) => {
  const theme = useTheme(Promotion);
  const { hideShortDesc, hideSpecialBuyLink } = theme.props;
  const { storeId } = useStore();
  const [mounted, setMounted] = useState(false);
  const hiddenPromosConfig = useConfigService(FEATURE_SWITCH_KEYS.pipHiddenPromotionTypes);
  const isPlpPromoMessageEnabled = pageType === 'BIP' || useConfigService(FEATURE_SWITCH_KEYS.plpPromoMessageEnabled);
  const isDailyDealsEnabled = useConfigService(FEATURE_SWITCH_KEYS.isDailyDealsEnabled);
  const hiddenPromos = hiddenPromosConfig
    ? JSON.parse(hiddenPromosConfig)
    : [];

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: promoData } = useDataModel('product', {
    variables: { itemId, storeId },
    skip: !!promotionData
  });

  const data = promotionData || promoData;

  const promotion = getPromoToDisplay({
    data,
    hiddenPromos,
    hideConditionalPromoDescription
  });
  const conditionalPromo = data?.product?.pricing?.conditionalPromotions?.[0];
  const isSpecialBuy = hasSpecialBuy(promotion);
  const specialBuy = isSpecialBuy ? getSpecialBuy(promotion, isDailyDealsEnabled) : null;
  const hasPlpPromoMessaging = CONDITIONAL_PROMO_PAGES.includes(pageType)
    && isPlpPromoMessageEnabled
    && conditionalPromo;
  const shortDesc = getShortDescription(promotion);
  const longDesc = getLongDescription(promotion);
  const start = getStartDate(promotion);
  const end = getEndDateWithTimezoneOffset(promotion, isSpecialBuy);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!promotion && !hasPlpPromoMessaging) { return null; }

  return (
    <div data-testid="promotion" data-component="Promotion">
      {isSpecialBuy && (
        <div className="sui-flex sui-mb-1">
          <div className="sui-inline-block sui-align-baseline sui-fill-brand sui-w-6 sui-h-6 sui-pr-8 sui-pt-2">
            <Clock size="large" color="brand" />
          </div>
          <div className="sui-pl-2">
            <Typography>{specialBuy?.text}&nbsp;
              <div className="sui-inline-block">
                {mounted && (
                  <CountdownTimer
                    isPromotionTimer
                    endDate={end}
                  />
                )}
              </div>
            </Typography>
            {!hideSpecialBuyLink && (
              <div className="sui-block">
                <Link href={specialBuy?.link} target={specialBuy?.link}>
                  {specialBuy?.description}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
      {(!isSpecialBuy && !hasPlpPromoMessaging && !hideShortDesc) && (
        <div className="sui-py-2 sui-cursor-pointer sui-inline-flex sui-items-center" data-testid="promotion-tooltip">
          <Typography
            variant="body-base"
            color="info"
            onClick={(event) => {
              event.preventDefault();
              setAnchorEl(event.currentTarget);
              analyticsPromoClickEvent();
            }}
          >{shortDesc}
          </Typography>
          <Popover
            id={open ? 'promotion-popover' : undefined}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            placement="top"
            title="Promotion Details"
          >
            <Typography component="div">
              <div data-component="PromotionTooltip" className="sui-text-sm">
                <span className="sui-pt-1 sui-pb-2 sui-block">{`Price Valid: ${start} - ${end}`}</span>
                {longDesc || shortDesc}
              </div>
            </Typography>
          </Popover>
        </div>
      )}
      {hasPlpPromoMessaging && (
        <PromoModelProvider
          itemId={itemId}
          promotion={conditionalPromo}
        >
          <PromoMessageProvider>
            <PromoMessaging />
          </PromoMessageProvider>
        </PromoModelProvider>
      )}
    </div>
  );
};

Promotion.displayName = 'Promotion';

Promotion.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    dataSources: string(),
    pricing: params({ storeId: string() }).shape({
      preferredPriceFlag: bool(),
      promotion: shape({
        dates: shape({
          start: string(),
          end: string()
        }),
        description: shape({
          shortDesc: string(),
          longDesc: string()
        }),
        experienceTag: string(),
        subExperienceTag: string()
      }),
      conditionalPromotions: arrayOf(shape({
        promotionId: string(),
        skuItemGroup: string(),
        promotionTags: arrayOf(string()),
        eligibilityCriteria: arrayOf(shape({
          itemGroup: string(),
          minThresholdVal: number({ float: true }),
          thresholdType: string()
        })),
        reward: shape({
          tiers: arrayOf(shape({
            minThresholdVal: number({ float: true }),
            thresholdType: string(),
            rewardVal: number({ float: true }),
            rewardType: string(),
            rewardLevel: string(),
            maxAllowedRewardAmount: number({ float: true })
          }))
        })
      }))
    })
  })
};

Promotion.propTypes = {
  /** The item */
  itemId: PropTypes.string.isRequired,
  /** To use the config service to hide certain promos */
  hideConditionalPromoDescription: PropTypes.bool,
  /** Used to hide PLP messaging if we are on PIP */
  pageType: PropTypes.string,
  promotionData: PropTypes.shape({})
};

Promotion.defaultProps = {
  hideConditionalPromoDescription: false,
  pageType: 'PIP',
  promotionData: null
};

Promotion.themeProps = {
  hideShortDesc: bool,
  hideSpecialBuyLink: bool
};

Promotion.defaultThemeProps = {
  hideShortDesc: false,
  hideSpecialBuyLink: false
};

export default Promotion;
